import { useUserStore, usePageStore } from '@/store';
import { mapState as rootMapState } from 'pinia';
import { defineAsyncComponent } from 'vue';
import util from '../../common/util';
import { Preview, PreviewNum, DEFAULT_HOUSE_PREVIEW } from '@/common/utils';
import HouseFormat, {
    mapHouseData,
    getBasic,
    getProviderText,
    getFlagtext
} from '@/common/house';
import NoDisclosure from '../../components/nodisclosure.vue';
import detailPop from '@/components/global/detail-pop/index.js';
import HouseMask from './mask.vue';
import TitleMask from './_component/title-mask.js';
import VerifyMask from '@/modules/md-listing-list/verify-mask.vue';
import { getHouseSchemaInfo } from '@/common/schemaInfo.js';
import signLog from '@/components/sign-log';
import houseTag from './_component/house-tag';
import { calcHouseLink, transformLink2WordPress, useHouseCollect } from './useHouse';
//which mlsOrgId to show "*" before sold date
const showStarMLS = [588]
export default {
    i18next: {
        ns: 'md-house'
    },
    props: {
        house: {
            type: Object,
            default: () => ({})
        },
        dir: {
            type: String,
            default: 'width'
        }
    },
    data: function() {
        return {
            defHouseImg: DEFAULT_HOUSE_PREVIEW,
            previewIndex: 1,
            vowAddress: ''
            // provided: ''
        };
    },
    computed: {
        ...rootMapState(usePageStore, {
            currencySymbol: store => store.currency.symbol,
            currencyAbbr: store => store.currency.abbr,
            listingSearchSwitch: store => store.page.listingSearchSwitch,
            page: 'processedGlobalData',
            forCMS: 'forCMS'
        }),
        ...rootMapState(useUserStore, {
            loginStatus: store => store.loginStatus,
            user: store => store.user,
            userEmailVerified: 'userEmailVerified'
        }),
        houseData({ house, page }) {
            return HouseFormat.call(this, house, page);
        },
        ...mapHouseData('houseData'),
        provided({ house, listingSearchSwitch }) {
            return listingSearchSwitch
                ? house.mlsOrg?.cardTemplate
                : getProviderText(house.mlsOrg?.cardTemplate, house);
        },
        flagtext({ page }) {
            return getFlagtext.call(this, {
                house: this.house,
                fmtData: this,
                globalData: page
            });
        },
        basic() {
            let bsc = getBasic.call(this, this.house);
            if (this.isShowMask) {
                bsc = bsc.map(item => {
                    return {
                        key: item.key,
                        text: item.text.replace(/[\d|.|,]{1,}/g, '***')
                    };
                });
            }
            return bsc;
        },
        sqft({ basic }) {
            return basic.find(it => it.key === 'sqft')?.text
        },
        beds({ house }) {
            return house.bedrooms < 1 ? '--' : house.bedrooms
        },
        baths({ house }) {
            return house.bathrooms < 1 ? '--' : house.bathrooms
        },
        seoSchema({ page }) {
            return getHouseSchemaInfo(this.house, {
                url: this.detailUrl,
                country: page.country,
                location: this.pageLocation,
                currencyAbbr: this.currencyAbbr,
                agentCreator: page.agentCreator
            });
        },
        isMobile() {
            return this.platform.isMobile;
        },
        wordPressSSR({ page }) {
            return page.wordPressSSR;
        },
        widget({ page }) {
            return page.widget;
        },
        previews() {
            return this.previewPictures?.length > 0
                ? this.previewPictures
                : [this.preview];
        },
        previewsJson() {
            return JSON.stringify(this.previews);
        },
        showAllPrice({ showListingPrice, showSoldPrice }) {
            return showListingPrice && showSoldPrice;
        },
        hidePrice({ showListingPrice, showSoldPrice, soldDate }) {
            return !showListingPrice && !showSoldPrice && soldDate;
        },
        inPopDetailPage() {
            return util.inPopDetailPage;
        },
        linkData() {
            let link = calcHouseLink(this.houseData)
            return {
                href: transformLink2WordPress(link),
                target: this.detailUrlTarget,
                'aria-label': this.address,
                self: this.inPopDetailPage
            };
        },
        mlsAlt() {
            return (this.house.mlsOrg && this.house.mlsOrg.name) || this.mlsid;
        },
        isShowMask({ userEmailVerified, noVow }) {
            return this.isProtected && !userEmailVerified && !noVow;
        },
        noVow({ page }) {
            return page.pageKey === 'agent-detail';
        },
        showSwitch({ page }) {
            let { switchNeedSign } = page; // Whether to switch property photos left or right after logging in
            return Boolean(switchNeedSign !== true || this.user);
        },
        exclusiveTag({ exclusive, isShowMask }) {
            if (exclusive && !isShowMask) {
                return {
                    className: exclusive?.style?.className,
                    value: exclusive?.value || ''
                };
            }
            return false;
        },
        tag({ listingTags, status, isShowMask }) {
            if ((listingTags?.length || status) && !isShowMask) {
                //如果是sold，则在售出日期前加上星号
                let pre = '';
                if (listingTags[0]?.style?.className === 'sold' && this.showStar) {
                    pre = '*';
                }
                return {
                    className: listingTags[0]?.style?.className || '',
                    value: pre + (listingTags[0]?.value || status)
                };
            }
            return false;
        },
        moreListing() {
            if (this.moreInfo) {
                return this.moreInfo();
            }
        },
        isMobileSize() {
            return this.platform.isMobile;
        },
        //whether to show "*" before sold date
        showStar({ house }) {
            return showStarMLS.includes(house.mlsOrgId)
        }
    },
    inject: {
        isInMap: {
            type: Boolean,
            default: false
        },
        moreInfo: {
            type: Function,
            default: null
        }
    },
    watch: {
        loginStatus(val) {
            if (val === -1) {
                this.collected = this.house.collectStatus = false;
                this.maskData();
            } else if (val === 1 && this.isShowMask) {
                this.vowAddress = this.$t('addressNotDisclosed');
            }
        },
        vow() {
            this.maskData();
        },
        ['house.' + Preview.origin](preview) {
            this[Preview.name] = preview;
        },
        ['house.' + PreviewNum.origin](preview) {
            this[PreviewNum.name] = preview;
        }
    },
    mounted() {
        const { collect } = useHouseCollect({ 
            collectSuccessCallback: () => {
                this.collected = this.house.collectStatus = !this.collected;
            }
        });
        this.collect = () => {
            collect(this.houseData);
        };
    },
    methods: {
        onMouseEnter() {
        },
        onMouseLeave() {},
        onPreviewChange(v) {
            this.previewIndex = v;
        },
        openDetail(houseLink, e) {
            let link = calcHouseLink(this.houseData)
            const curLink = transformLink2WordPress(link);
            if (this.isMobile) {
                e.preventDefault();
                e.stopPropagation();
                if (this.inPopDetailPage) {
                    window.parent.__curDetailPop &&
                        window.parent.__curDetailPop.setLink(curLink);
                } else {
                    let openAnimation = this.isInMap
                        ? 'bottomToUp'
                        : 'fadeInRight';
                    //  mobile terminal widget Fail detailPop jump
                    this.detailPopIns = this.widget
                        ? window.open(this.getWidgetUrl(curLink))
                        : detailPop.open(curLink, openAnimation);
                }
            }
        },
        getWidgetUrl(url) {
            if (/global_domain/.test(this.pageLocation.search)) {
                url =
                    url.indexOf('?') > -1
                        ? `${url}&global_domain=${this.page.domainName}`
                        : `${url}?global_domain=${this.page.domainName}`;
                return `https://${window.location.host}${url}`;
            } else {
                return url;
            }
        },
        maskData() {
            if (this.isShowMask) {
                this.vowAddress = this.$t('addressNotDisclosed');
            }
        },
        register(options) {
            let event = options && options.event;
            if (event) {
                event.stopPropagation();
                event.preventDefault();
            }
            signLog.register({
                allowClose: true,
                languageTextFn: {
                    agreementHtml: _ => this.$t('byContinuingYouAgree')
                },
                onregister: (_user, _inquiry) => {
                    return false;
                },
                onsuccess: () => {
                    return false; //  Do not pop up the default prompt box
                }
            });
        },
        getHouseTagValue() {
            if (this.showStar) {
                return this.$t('md-house.soldOnWith', { soldDate: this.$ft.formatDate(this.soldDate, 'MM/dd/yyyy') })
            } else {
                return this.$t('md-house.soldOn', { soldDate: this.$ft.formatDate(this.soldDate, 'MM/dd/yyyy') })
            }
        }
    },
    created() {
        this.maskData();
    },
    components: {
        NoDisclosure,
        imgSwiper: defineAsyncComponent(() =>
            import(
                /* webpackChunkName: "common/img-swiper" */ '@/components/img-swiper.vue'
            )),
        HouseMask,
        TitleMask,
        houseTag,
        VerifyMask
    }
};
